function formatDashboardCurrency(value) {
    return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(Number(value) || 0)
}

function formatDashboardInteger(value) {
    return new Intl.NumberFormat("en-US", {
        maximumFractionDigits: 0,
    }).format(Number(value) || 0)
}

function normalizeDashboardCheckPayload(payload) {
    if (Array.isArray(payload)) {
        return { rows: payload, error: false }
    }

    if (!payload || typeof payload !== "object") {
        return { rows: [], error: false }
    }

    return {
        rows: Array.isArray(payload.rows) ? payload.rows : [],
        error: payload.status === "error" || payload.error === true,
    }
}

function storeHasQaSeededAgcoEvent(store) {
    const events = store && store.events ? store.events : {}
    return Object.values(events).some((event) => !!(event && event.qaSeeded))
}

function resetDashboardSalesScope(context) {
    context.salesByWarehouseSnapshot = null
    context.topProductsSnapshot = null
    context.statsComparison = {
        products: { percentage: 0, trend: "neutral" },
        customers: { percentage: 0, trend: "neutral" },
        orders: { percentage: 0, trend: "neutral" },
        revenue: { percentage: 0, trend: "neutral" },
        averageOrderValue: { percentage: 0, trend: "neutral" },
    }
    context.dashboardPeriods = { current: {}, previous: {} }
    context.dashboardCheckErrors.warehouseSales = false
    context.dashboardCheckErrors.productSales = false
    context.dashboardCheckErrors.stats = false
    context.setData({ dashboardStats: null })
    return context.fetchDashboardData()
}

function dashboardReportTarget(context, path) {
    const query = {
        date_range_start: context.dateRange?.startDate,
        date_range_end: context.dateRange?.endDate,
    }
    if (context.includePilotSales) query.includePilotSales = "true"
    return { path, query }
}

function buildDashboardActionItems(context) {
    const items = []
    const agcoHealthSnapshot = context.agcoHealthSnapshot || []
    const salesByWarehouseSnapshot = context.salesByWarehouseSnapshot || []
    const topProductsSnapshot = context.topProductsSnapshot || []
    const dashboardCheckErrors = context.dashboardCheckErrors || {}
    const statsComparison = context.statsComparison || {}
    const dashboardPeriods = context.dashboardPeriods || {}
    const dashboardStats = context.dashboardStats || {}
    const revenueComparison = statsComparison.revenue || {}
    const currentPeriod = dashboardPeriods.current || {}
    const previousPeriod = dashboardPeriods.previous || {}

    const agcoProblemStores = agcoHealthSnapshot.filter((store) => !store.healthy)
    const qaSeededAgcoStores = agcoHealthSnapshot.filter((store) =>
        storeHasQaSeededAgcoEvent(store)
    )
    const unknownWarehouse = salesByWarehouseSnapshot.find((warehouse) => {
        const name = (warehouse.warehouse_name || "").toLowerCase()
        return warehouse.warehouse_id === "unknown" || name === "unknown warehouse"
    })
    const testProducts = topProductsSnapshot.filter((product) =>
        /^test product\b/i.test(product.product_name || "")
    )
    const revenueTrend = Number(revenueComparison.percentage || 0)
    const currentRevenue = Number(currentPeriod.revenue || dashboardStats.todayRevenue || 0)
    const previousRevenue = Number(previousPeriod.revenue || 0)

    if (dashboardCheckErrors.stats) {
        items.push({
            id: "dashboard-stats-load-error",
            severity: "high",
            label: "Sales",
            title: "Dashboard sales metrics could not load",
            description: "The dashboard could not verify revenue, order, or average-order-value trends for this period.",
            details: ["Stats check unavailable", "Refresh dashboard"],
            cta: "View report",
            to: dashboardReportTarget(context, "/reports/sale-report"),
        })
    }

    if (dashboardCheckErrors.agcoHealth) {
        items.push({
            id: "agco-health-load-error",
            severity: "high",
            label: "Compliance",
            title: "AGCO status could not load",
            description: "The dashboard could not verify AGCO submission health for this period.",
            details: ["Check /agco-logs/health", "Refresh dashboard"],
            cta: "View logs",
            to: "/agco-logs/warehouses",
        })
    }

    if (agcoProblemStores.length) {
        const storeNames = agcoProblemStores.slice(0, 4).map((store) => store.warehouseName)
        const remainingStores = agcoProblemStores.length - storeNames.length
        const details = [
            ...storeNames,
            ...(remainingStores > 0 ? [`+${remainingStores} more`] : []),
            ...(qaSeededAgcoStores.length ? ["Local QA seed data"] : []),
        ]

        items.push({
            id: "agco-health",
            severity: "high",
            label: "Compliance",
            title: "AGCO submissions need review",
            description: `${agcoProblemStores.length} ${agcoProblemStores.length === 1 ? "store has" : "stores have"} failed, overdue, or missing AGCO submission data.`,
            details,
            cta: "View logs",
            to: "/agco-logs/warehouses",
        })
    }

    if (dashboardCheckErrors.warehouseSales) {
        items.push({
            id: "warehouse-sales-load-error",
            severity: "high",
            label: "Data",
            title: "Sales by store could not load",
            description: "The dashboard could not verify whether sales are assigned to valid stores.",
            details: ["Store assignment check unavailable", "Refresh dashboard"],
            cta: "Open report",
            to: dashboardReportTarget(context, "/reports/sale-by-location"),
        })
    }

    if (unknownWarehouse && Number(unknownWarehouse.revenue) > 0) {
        const unknownRevenue = Number(unknownWarehouse.revenue || 0)
        const unknownOrders = Number(unknownWarehouse.orders || 0)
        items.push({
            id: "unknown-warehouse",
            severity: "high",
            label: "Data",
            title: "Sales posted without a store",
            description: `${formatDashboardCurrency(unknownRevenue)} across ${formatDashboardInteger(unknownOrders)} orders is assigned to Unknown Warehouse.`,
            details: [
                `${formatDashboardCurrency(unknownRevenue)} unassigned`,
                `${formatDashboardInteger(unknownOrders)} orders`,
                "Fix invoice warehouse mapping",
            ],
            cta: "Open report",
            to: dashboardReportTarget(context, "/reports/sale-by-location"),
        })
    }

    if (dashboardCheckErrors.productSales) {
        items.push({
            id: "product-sales-load-error",
            severity: "medium",
            label: "Catalog",
            title: "Top products could not load",
            description: "The dashboard could not verify whether test products appear in top sellers.",
            details: ["Product naming check unavailable", "Refresh dashboard"],
            cta: "Review products",
            to: dashboardReportTarget(context, "/reports/sale-by-product"),
        })
    }

    if (testProducts.length) {
        const examples = testProducts.slice(0, 3).map((product) => product.product_name)
        const remainingProducts = testProducts.length - examples.length
        items.push({
            id: "test-products",
            severity: "medium",
            label: "Catalog",
            title: "Test products are in sales data",
            description: `${testProducts.length} test-named ${testProducts.length === 1 ? "product appears" : "products appear"} in top sellers.`,
            details: [
                ...examples,
                ...(remainingProducts > 0 ? [`+${remainingProducts} more`] : []),
            ],
            cta: "Review products",
            to: dashboardReportTarget(context, "/reports/sale-by-product"),
        })
    }

    if (revenueComparison.trend === "negative" && revenueTrend <= -20) {
        items.push({
            id: "revenue-drop",
            severity: "medium",
            label: "Sales",
            title: "Revenue is sharply below comparison",
            description: `Revenue is ${Math.abs(revenueTrend).toFixed(1)}% below the previous period for the selected range.`,
            details: [
                `Current ${formatDashboardCurrency(currentRevenue)}`,
                previousRevenue > 0 ? `Previous ${formatDashboardCurrency(previousRevenue)}` : "No previous revenue",
                `Change ${Math.abs(revenueTrend).toFixed(1)}%`,
            ],
            cta: "View report",
            to: dashboardReportTarget(context, "/reports/sale-report"),
        })
    }

    const severityRank = { high: 0, medium: 1, low: 2 }
    return items.sort((a, b) => severityRank[a.severity] - severityRank[b.severity])
}

function isDashboardActionItemsLoading(context) {
    return !context.dashboardStatsCheckComplete ||
        context.salesByWarehouseSnapshot === null ||
        context.topProductsSnapshot === null ||
        context.agcoHealthSnapshot === null
}

export {
    buildDashboardActionItems,
    formatDashboardCurrency,
    formatDashboardInteger,
    isDashboardActionItemsLoading,
    normalizeDashboardCheckPayload,
    resetDashboardSalesScope,
    storeHasQaSeededAgcoEvent,
}
